import axios from 'axios';
import { createAuthFetch, addAuthHeaders } from '@umd-dit/oidc-core';

// Get API URL from environment
const API_URL = import.meta.env.VITE_API_URL;

// Create the authenticated fetch function from the OIDC core library
const authFetch = createAuthFetch();

// Create base API instance
export const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
});

// Add request interceptor to add auth headers using the OIDC core's addAuthHeaders function
api.interceptors.request.use((config) => {
  console.log(`API Request to ${config.url}`);
  
  // Get the current headers as a plain object
  const currentHeaders = config.headers ? { ...config.headers } : {};
  
  // Convert axios headers to headers that addAuthHeaders expects
  const fetchOptions: RequestInit = {
    headers: currentHeaders as Record<string, string>,
  };
  
  // Use the OIDC core's addAuthHeaders function to add the proper auth headers
  const optionsWithAuth = addAuthHeaders(fetchOptions);
  
  // Apply the headers from optionsWithAuth to the axios config
  if (optionsWithAuth.headers) {
    const authHeaders = optionsWithAuth.headers;
    
    if (authHeaders instanceof Headers) {
      // Convert Headers object to plain object
      authHeaders.forEach((value, key) => {
        if (config.headers) {
          config.headers[key] = value;
        }
      });
    } else if (typeof authHeaders === 'object') {
      // Handle plain object headers
      Object.entries(authHeaders).forEach(([key, value]) => {
        if (config.headers && typeof value === 'string') {
          config.headers[key] = value;
        }
      });
    }
    
    console.log('Authorization headers added successfully');
  }
  
  return config;
});

// For fetch-based API calls, export the authFetch function directly
export { authFetch };

// Add response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Add global error handling here if needed
    if (axios.isAxiosError(error)) {
      if (error.response?.status === 401) {
        // Redirect to login or handle unauthorized error
        console.error('Unauthorized access - JWT might be missing or invalid');
      }
    }
    return Promise.reject(error);
  }
);

// Helper function for making authenticated API calls with the fetch API
export const apiFetch = async <T = any>(
  endpoint: string, 
  options: RequestInit = {}
): Promise<T> => {
  const url = `${API_URL}${endpoint}`;
  const response = await authFetch(url, options);
  
  if (!response.ok) {
    throw new Error(`API error: ${response.status} ${response.statusText}`);
  }
  
  // For non-JSON responses, return the Response object
  const contentType = response.headers.get('content-type');
  if (!contentType || !contentType.includes('application/json')) {
    return response as unknown as T;
  }
  
  return response.json();
};
